// THE TRADE CARD SYSTEM (UI polish, 22 Oct 2026). One presentation for every strategy - Futures Spread, BUY Options,
// SELL Options, Iron Condor, Box Spread - modelled on the expanded Options card. Every card is built from these parts,
// in this order:
//
//   TradeCard            the frame (status accent on the left edge, warning outline)
//    CardHead            toggle · STRATEGY pill · symbol · expiry · MODE pill · status · market-data pill · extra badges
//    (confirmations)     inline confirmations the card asks for
//    AlertStrip          warnings - always visible, collapsed or expanded
//    CompactSummary      ALWAYS: P&L headline · one risk / target metric · size · actual average entry · market data · scale bar
//    CardFooter          routine actions · Analyse Trade · Details  ||  Square Off (set apart, last)
//    TradeDetailsModal   DETAILS (30 Oct 2026): the full trade - Overview · Contracts · Economics · Execution · Risk & State
//                        (CardSections) · strategy blocks · audit - in a modal. Cards no longer grow vertically.
//
// Display only: the parts lay out what each card already computes. No figure, action, handler or confirmation changes.
import React, { useEffect } from "react";
import { BarChart3, X } from "lucide-react";
import ProtectionScale from "@/components/trade/ProtectionScale";
import { AlertStrip, BTN, CardToggle, KV, Section } from "@/components/options/CardParts";
import { modeLabel, modeText, modeTitle } from "@/lib/modes";
import { NET_BIDASK_LABEL, NET_BIDASK_NOTE, NET_LTP_LABEL, NET_LTP_NOTE } from "@/lib/netPnl";

export const ANALYSE_LABEL = "Analyse Trade";
export const SQUARE_OFF_LABEL = "Square Off";
export { AlertStrip, BTN, CardToggle, KV, Section };

// ── colours: one rule for every P&L on every card ──
export const pnlTone = (v) => (v === null || v === undefined || Number(v) === 0 || Number.isNaN(Number(v)) ? "text-[var(--term-secondary)]"
  : Number(v) > 0 ? "text-[#00C805]" : "text-[#FF3B30]");
const money = (v) => `₹${Math.abs(Number(v)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const signedMoney = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—"
  : `${Number(v) > 0 ? "+" : Number(v) < 0 ? "−" : ""}${money(v)}`);

// ── what a P&L figure IS. The label and the basis line always say it; nothing theoretical reads as realisable. ──
export const PNL_KIND = {
  LIQUIDATION: { label: "Net P&L to close", note: "estimated · executable now" },
  INDICATIVE: { label: "P&L at last prices", note: "indicative · not executable" },
  MARK: { label: "Gross P&L", note: "marked at last traded prices" },
  ESTIMATE: { label: "Unrealised P&L", note: "estimate" },
  REALISED: { label: "Realised net P&L", note: "final" },
  EXPIRY: { label: "Expiry payoff", note: "theoretical · at expiry only" },
  NONE: { label: "P&L", note: "nothing held" },
};

/** The headline P&L: kind label, big signed value, then basis · charges · time. */
export function PnlFigure({ kind = "LIQUIDATION", value, basis, charges, at, testId, big = true, label, dim }) {
  const k = PNL_KIND[kind] || PNL_KIND.LIQUIDATION;
  const meta = [basis || k.note, charges, at ? `as of ${at}` : null].filter(Boolean).join(" · ");
  return (
    <div className="min-w-0" title={meta}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)] whitespace-nowrap">{label || k.label}</div>
      <div className={`mono font-bold leading-tight ${big ? "text-lg" : "text-sm"} ${dim ? "text-[var(--term-muted)]" : pnlTone(value)}`} data-testid={testId}>{signedMoney(value)}</div>
      <div className="text-[10px] text-[var(--term-muted)] leading-tight" data-testid={testId ? `${testId}-basis` : undefined}>{meta}</div>
    </div>
  );
}

/** The two NET P&L figures every card shows (27 Oct 2026): NET at LTP (headline - what the Auto Square-Off target
 *  compares) and NET at bid/ask beneath it. A finished trade shows its realised figure once. */
export function NetPnlPair({ ltp, bidask, bidaskNote, at, dim, testId, finished, realisedLabel = "Realised net P&L", ltpNote }) {
  if (finished) {
    return <PnlFigure kind="REALISED" value={ltp} label={realisedLabel} testId={testId} />;
  }
  return (
    <div className="min-w-0" data-testid={testId ? `${testId}-pair` : undefined}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)] whitespace-nowrap" title={NET_LTP_NOTE}>{NET_LTP_LABEL}</div>
      <div className={`mono font-bold leading-tight text-lg ${dim ? "text-[var(--term-muted)]" : pnlTone(ltp)}`} title={NET_LTP_NOTE} data-testid={testId}>{signedMoney(ltp)}</div>
      {ltpNote && <div className="text-[10px] leading-tight text-[#B45309]" data-testid={testId ? `${testId}-ltp-note` : undefined}>{ltpNote}</div>}
      <div className="text-[11px] leading-tight whitespace-nowrap" title={`${NET_BIDASK_NOTE}${bidaskNote ? ` (${bidaskNote})` : ""}`}>
        <span className="text-[var(--term-muted)]">{NET_BIDASK_LABEL} </span>
        <span className={`mono font-semibold ${dim ? "text-[var(--term-muted)]" : pnlTone(bidask)}`} data-testid={testId ? `${testId}-bidask` : undefined}>{signedMoney(bidask)}</span>
      </div>
      <div className="text-[10px] text-[var(--term-muted)] leading-tight">after entry + est. exit charges{at ? ` · as of ${at}` : ""}</div>
    </div>
  );
}

// ── the frame ──
const ACCENT = { good: "border-l-[#00C805]", busy: "border-l-[#2563EB]", warn: "border-l-[#B45309]", bad: "border-l-[#FF3B30]",
  done: "border-l-[var(--term-border-strong)]", idle: "border-l-[var(--term-border-strong)]" };

export function TradeCard({ testId, tone = "idle", danger, highlight, children, innerRef, dataStatus }) {
  return (
    <div ref={innerRef} data-testid={testId} data-card="trade" data-status={dataStatus}
      className={`term-panel rounded-md border border-[var(--term-border)] border-l-4 ${ACCENT[tone] || ACCENT.idle} min-w-0
        ${danger ? "!border-[#FF3B30]/50" : ""} ${highlight ? "ring-2 ring-[#2563EB]/60" : ""}`}>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}

export function ModePill({ env, testId }) {
  const live = modeText(env) === "LIVE";
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap ${live ? "bg-[#FF3B30]/15 text-[#FF3B30]" : "bg-[#7c3aed]/15 text-[#7c3aed]"}`}
    title={modeTitle(env)} data-testid={testId}>{modeLabel(env).toUpperCase()}</span>;
}

const STATUS_CLS = { good: "text-[#008a04]", busy: "text-[#2563EB]", warn: "text-[#B45309]", bad: "text-[#FF3B30]", done: "text-[var(--term-muted)]", idle: "text-[var(--term-secondary)]" };
export function StatusText({ text, tone = "idle", testId, title }) {
  return <span className={`text-xs font-bold whitespace-nowrap ${STATUS_CLS[tone] || ""}`} data-testid={testId} title={title}>{text}</span>;
}

/** strategy = {label, cls}; title = symbol / contracts; sub = expiry; badges = extra pills (market data, auto exit, …). */
export function CardHead({ toggle, strategy, title, sub, mode, status, badges }) {
  return (
    <div className="flex items-center gap-x-2 gap-y-1 flex-wrap min-w-0" data-testid="card-head">
      {toggle}
      {strategy && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap ${strategy.cls || "bg-[var(--term-s3)]"}`}
        data-testid={strategy.testId}>{strategy.label}</span>}
      <b className="text-sm min-w-0 break-words">{title}</b>
      {sub && <span className="text-xs text-[var(--term-muted)] mono whitespace-nowrap">{sub}</span>}
      {mode}
      {status}
      {badges}
    </div>
  );
}

/** COLLAPSED: the same five places on every card. Each of risk / size / entry / data = {label, value, cls, title, testId}. */
export function CompactSummary({ pnl, risk, size, entry, data, scale, testId = "card-compact" }) {
  const Cell = ({ c }) => c ? (
    <div className="min-w-0" title={c.title}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-secondary)] whitespace-nowrap">{c.label}</div>
      <div className={`mono text-[12px] font-semibold leading-tight break-words ${c.cls || ""}`} data-testid={c.testId}>{c.value ?? "—"}</div>
      {c.sub && <div className="text-[10px] text-[var(--term-muted)] leading-tight" data-testid={c.subTestId}>{c.sub}</div>}
    </div>) : <div />;
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 ${scale ? "lg:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))_auto]" : "lg:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]"} gap-x-5 gap-y-2 items-start`} data-testid={testId}>
      <div className="col-span-2 sm:col-span-1">{pnl}</div>
      <Cell c={risk} /><Cell c={size} /><Cell c={entry} /><Cell c={data} />
      {scale && <div className="min-w-0" data-testid={`${testId}-scale`}>{scale}</div>}
    </div>
  );
}

/** The Common Trade Panel's coloured bar on a card: row = {key, scale, spot} in the panel's shape (ProtectionScale). */
export function CardScale({ row }) {
  return <div title="Band / strikes / spread with the current spot or spread"><ProtectionScale row={row} /></div>;
}

/** DETAILS: the whole trade in one modal (every card, every segment). `overview` = the scale and identity line;
 *  children = CardSections + the strategy blocks the card used to expand inline. Escape / backdrop / ✕ close it. */
export function TradeDetailsModal({ open, onClose, title, sub, overview, children, testId }) {
  return (
    <CardDialog open={open} onClose={onClose} title={title} sub={sub} xwide testId={testId}>
      {overview && <section className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-sm border border-[var(--term-border)] p-2.5" data-testid={testId ? `${testId}-overview` : undefined}>
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--term-secondary)] w-full">Overview</h3>{overview}</section>}
      {children}
    </CardDialog>
  );
}

/** Sections in the details modal: the titled sections, side by side on wide screens (Contracts · Economics · Execution · Risk & State …). */
export function CardSections({ children, testId, cols }) {
  return <div className={`grid grid-cols-1 md:grid-cols-2 ${cols || "xl:grid-cols-4"} border border-[var(--term-border)] rounded-sm`} data-testid={testId}>{children}</div>;
}

/** A titled block under the sections (strategy-specific: pair table, legs, rules, ladder …). */
export function CardBlock({ title, hint, children, testId, right }) {
  return (
    <section className="border border-[var(--term-border)] rounded-sm p-2.5 space-y-2 min-w-0" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--term-secondary)]">{title}</h3>
        {hint && <span className="text-[10px] text-[var(--term-muted)]">{hint}</span>}
        {right && <span className="ml-auto">{right}</span>}
      </div>
      {children}
    </section>
  );
}

/** Routine actions, then Analyse Trade and Details, then - set apart at the end - Square Off / destructive actions. */
export function CardFooter({ children, analyse, details, danger, testId = "card-actions" }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-[var(--term-border)]" data-testid={testId}>
      {children}
      {(analyse || details) && <span className="flex items-center gap-1.5" data-testid="card-actions-inspect">{analyse}{details}</span>}
      {danger && <span className="ml-auto pl-2 border-l border-[var(--term-border-strong)] flex items-center gap-1.5" data-testid="card-actions-exit">{danger}</span>}
    </div>
  );
}

export function AnalyseTradeButton({ onClick, testId, disabled, title }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      title={title || "Where this trade can go: payoff, breakevens and scenarios from its confirmed fills"}
      className={`${BTN} inline-flex items-center gap-1`}><BarChart3 className="h-3 w-3" />{ANALYSE_LABEL}</button>
  );
}

export function DetailsButton({ open, onClick, testId, label = "Details" }) {
  return <button type="button" className={BTN} onClick={onClick} aria-expanded={!!open} data-testid={testId}>{label}</button>;
}

export function SquareOffButton({ onClick, testId, disabled, label = SQUARE_OFF_LABEL, title }) {
  return <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} title={title}
    className={`${BTN} border-[#FF3B30] text-[#FF3B30]`}>{label}</button>;
}

/** An inline confirmation (the card's own question, unchanged). */
export function ConfirmRow({ text, onYes, onNo, testId, yesLabel = "Yes", yesTestId }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs rounded-sm border border-[#FF3B30]/50 bg-[#FF3B30]/5 p-2" data-testid={testId}>
      <span className="min-w-0">{text}</span>
      <button className={`${BTN} border-[#FF3B30] text-[#FF3B30]`} data-testid={yesTestId} onClick={onYes}>{yesLabel}</button>
      <button className={BTN} onClick={onNo}>No</button>
    </div>
  );
}

/** A centred dialog for analysis / orders; Escape and the backdrop close it. */
export function CardDialog({ open, onClose, title, sub, children, testId, wide, xwide }) {
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-2 sm:p-6 overflow-auto" onClick={onClose} data-testid={testId}>
      <div className={`term-panel rounded-md border border-[var(--term-border-strong)] w-full ${xwide ? "max-w-7xl" : wide ? "max-w-5xl" : "max-w-2xl"} p-3 sm:p-4 space-y-3 min-w-0`}
        role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <div className="text-sm font-bold">{title}</div>
            {sub && <div className="text-[11px] text-[var(--term-muted)] mono truncate">{sub}</div>}
          </div>
          <button className="ml-auto p-1 rounded-sm hover:bg-[var(--term-hover)]" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
